import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { getR2ImageUrl, fetchCardByExactName } from '../utils/scryfallApi';
import { sanitizeThemeName } from '../utils/sanitizeName';
import '../styles/ThemeList.css';

// Format theme name for API requests
const formatThemeName = (theme) => {
  return sanitizeThemeName(theme);
};

// Check if a card name matches the theme keywords
const cardMatchesTheme = (cardName, themeName) => {
  const themeWords = themeName.toLowerCase().split(/\s+/);
  const cardNameLower = cardName.toLowerCase();
  
  for (const word of themeWords) {
    if (word.length < 3) continue;
    if (cardNameLower.includes(word)) {
      return true;
    }
  }
  return false;
};

// Score a card for how representative it is of a theme (no Scryfall needed!)
const scoreCard = (card, themeName, isHighSynergy) => {
  let score = 0;
  
  // Synergy is the main factor
  score += (card.Synergy || 0) * 100;
  
  // High synergy category bonus
  if (isHighSynergy) {
    score += 50;
  }
  
  // Theme keyword match in card name
  if (cardMatchesTheme(card.Name, themeName)) {
    score += 75;
  }
  
  // Popularity tiebreaker
  score += (card.Popularity || 0) / 1000;
  
  return score;
};

const ThemeList = ({ themes, commander, onThemeSelect, commanderName, commanderImageUrl, partnerCommander }) => {
  const [themeCards, setThemeCards] = useState({});
  const [loading, setLoading] = useState({});

  useEffect(() => {
    if (!themes || themes.length === 0) return;

    setThemeCards({});
    const loadingState = {};
    themes.forEach(theme => { loadingState[theme] = true; });
    setLoading(loadingState);

    // Fetch all theme breakdowns in parallel first
    const fetchAllThemeCards = async () => {
      // Fetch all theme data in parallel (fast!)
      const themeDataPromises = themes.map(async (theme) => {
        const formattedTheme = formatThemeName(theme);
        try {
          const url = `${API_URL}/v1/commander/${commander}/themes/${formattedTheme}`;
          const response = await fetch(url);
          if (!response.ok) return { theme, data: null };
          const data = await response.json();
          return { theme, data };
        } catch {
          return { theme, data: null };
        }
      });

      const allThemeData = await Promise.all(themeDataPromises);
      
      // Now process sequentially to pick unique cards (no API calls, just scoring)
      const usedCards = new Set();
      
      for (const { theme, data } of allThemeData) {
        if (!data) {
          setLoading(prev => ({ ...prev, [theme]: false }));
          continue;
        }

        // Build and score candidates (excluding already used cards)
        let bestCard = null;
        let bestScore = -Infinity;

        for (const cardType of data) {
          const isHighSynergy = cardType.CardType?.toLowerCase().includes('synergy');
          for (const card of cardType.Cards || []) {
            if (usedCards.has(card.Name)) continue;
            
            const score = scoreCard(card, theme, isHighSynergy);
            if (score > bestScore) {
              bestScore = score;
              bestCard = card;
            }
          }
        }

        if (bestCard) {
          usedCards.add(bestCard.Name);
          const r2Url = getR2ImageUrl(bestCard.Name);

          setThemeCards(prev => ({
            ...prev,
            [theme]: { 
              card: bestCard, 
              imageUrl: r2Url
            }
          }));
        }
        
        setLoading(prev => ({ ...prev, [theme]: false }));
      }
    };

    fetchAllThemeCards();
  }, [themes, commander]);

  return (
    <div className="theme-list-container">
      {/* Commander Card Display */}
      <div className="commander-display">
        <div className="commander-card-wrapper">
          <img 
            src={commanderImageUrl} 
            alt={commanderName}
            className="commander-card-image"
            onError={(e) => {
              // Fallback to Scryfall if the image fails
              if (!e.target.dataset.triedFallback) {
                e.target.dataset.triedFallback = 'true';
                e.target.src = `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(commanderName)}&format=image&version=normal`;
              }
            }}
          />
          {partnerCommander && (
            <img 
              src={partnerCommander.image ? partnerCommander.image.replace('art_crop', 'normal') : `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(partnerCommander.name)}&format=image&version=normal`}
              alt={partnerCommander.name}
              className="commander-card-image partner-card"
              onError={(e) => {
                if (!e.target.dataset.triedFallback) {
                  e.target.dataset.triedFallback = 'true';
                  e.target.src = `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(partnerCommander.name)}&format=image&version=normal`;
                }
              }}
            />
          )}
        </div>
        <div className="commander-info">
          <h3 className="commander-label">Your Commander</h3>
          <p className="commander-name">{commanderName}</p>
          {partnerCommander && <p className="partner-name">& {partnerCommander.name}</p>}
        </div>
      </div>
      
      {/* Theme Grid */}
      <div className="theme-grid">
        {themes.map((theme) => (
          <div key={theme} className="theme-item" onClick={() => onThemeSelect(commander, theme)}>
            <div className="theme-header">
              <h3 className="theme-name">{theme}</h3>
            </div>
            <div className="theme-card">
              {loading[theme] ? (
                <div className="theme-card-placeholder loading">Loading...</div>
              ) : themeCards[theme] ? (
              <img
                src={themeCards[theme].imageUrl}
                alt={themeCards[theme].card.Name}
                className="theme-card-image"
                title={themeCards[theme].card.Name}
                onError={(e) => {
                  // R2 missing — resolve to the Scryfall image CDN via a bounded, rate-limited
                  // metadata lookup, NOT the api.scryfall.com format=image redirect (rate-limited,
                  // and one image per theme would burst it).
                  const cardName = themeCards[theme]?.card?.Name;
                  if (cardName && !e.target.dataset.triedScryfall) {
                    e.target.dataset.triedScryfall = 'true';
                    fetchCardByExactName(cardName).then((data) => {
                      const url = data && !data._error
                        ? (data.image_uris?.art_crop || data.image_uris?.normal || data.card_faces?.[0]?.image_uris?.art_crop)
                        : null;
                      if (url) {
                        setThemeCards(prev => prev[theme]
                          ? { ...prev, [theme]: { ...prev[theme], imageUrl: url } }
                          : prev);
                      }
                    }).catch(() => {});
                  }
                }}
              />
            ) : (
              <div className="theme-card-placeholder">No card data</div>
            )}
          </div>
        </div>
      ))}
      </div>
    </div>
  );
};

export default ThemeList;
